import { useEffect, useState } from "react"
import { Card, CardMedia, Typography, Box, Collapse, Button, Stack, Skeleton } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCaretDown, faCaretUp, faCircleCheck } from "@fortawesome/pro-solid-svg-icons"
import { faInfoCircle, faClock } from "@fortawesome/pro-light-svg-icons"
import { useDateTime } from "../../../../../localization/useDateTime"
import { ResourceItem } from "../../weekMenuTypes"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useResourceDetailsDialog } from "../../../../../../shared/components/createOrder/components/resource/resourceBookingCalendars/useResourceDetailsDialog"
import { ResourceDetailsDialog } from "../../../../../../shared/components/dialogs/resourceDetailsDialog/ResourceDetailsDialog"

type ListComponentProps = Readonly<{
    availableResources?: ResourceItem[]
    onSelect: (id: number) => void
    selectedIds: ReadonlyArray<number>
    onTimeSelection: (startTime: string | null, endTime: string | null) => void
}>

const useStyles = makeStyles()(() => ({
    card: {
        display: "flex",
        flexDirection: "column",
        marginBottom: "1rem",
        border: "1px solid",
        borderColor: tailwindPalette.slate[300],
        borderRadius: ".75rem",
        boxShadow: "none",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
            boxShadow: "0 .25rem .75rem rgba(0, 0, 0, 0.1)",
        },
    },
    selected: {
        borderColor: tailwindPalette.emerald[500],
        backgroundColor: tailwindPalette.emerald[50],
        boxShadow: "0 .25rem .75rem rgba(0, 0, 0, 0.2)",
    },
    content: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: ".75rem",
    },
    skeleton: {
        width: "100%",
        height: "6rem",
        borderRadius: ".75rem",
        boxShadow: "0 .25rem .75rem rgba(0, 0, 0, 0.2)",
    },
    textContainer: {
        flex: 1,
        display: "flex",
    },
    dateChip: {
        backgroundColor: "#F1F5F9",
        padding: "0.5rem 1rem",
        borderRadius: "1rem",
        fontSize: "0.875rem",
        color: "#334155",
        fontWeight: 500,
        width: "fit-content",
    },
    timeBlock: {
        padding: ".25rem .625rem ",
        borderRadius: ".625rem",
        width: "fit-content",
        backgroundColor: tailwindPalette.slate[100],
    },
    timeBlockSelected: {
        backgroundColor: tailwindPalette.emerald[100],
    },
    collapseContent: {
        padding: ".75rem 0",
        marginLeft: "12rem",
        marginRight: ".75rem",
        borderTop: `1px solid ${tailwindPalette.slate[300]}`,
        display: "flex",
        alignItems: "center",
    },
    infoButton: {
        cursor: "pointer",
        marginLeft: ".5rem",
        color: "#64748B",
        ":hover": {
            color: "#94A3B8",
        },
    },
    selectButton: {
        height: "2.5rem",
        backgroundColor: tailwindPalette.slate[800],
        ":hover": {
            backgroundColor: tailwindPalette.slate[700],
        },
        textTransform: "none",
        borderRadius: ".75rem",
    },
    unselectButton: {
        height: "2.5rem",
        color: "black",
        textTransform: "none",
        borderRadius: ".75rem",
        backgroundColor: "white",
        border: "1px solid #CBD5E1",
        ":hover": {
            border: "1px solid #CBD5E1",
            backgroundColor: tailwindPalette.slate[50],
        },
    },
    image: {
        width: "9.75rem",
        height: "5.5rem",
        borderRadius: ".5rem",
    },
    caretButton: {
        width: "1rem",
        color: "black",
        height: "1rem",
    },
    selectedIcon: {
        marginLeft: ".5rem",
        color: tailwindPalette.emerald[500],
    },
    disabled: {
        opacity: 0.5,
        cursor: "not-allowed",
    },
}))

function RoomListItem({
    resource,
    bookingSuggestion,
    isSelected,
    onSelect,
    isDisabled,
}: ResourceItem & { isSelected: boolean; isDisabled: boolean; onSelect: (id: number) => void }) {
    const dateTimeFactory = useDateTime()
    const { classes, cx } = useStyles()
    const [expanded, setExpanded] = useState(false)
    const { resourceId, openResourceDetailsDialog, closeResourceDetailsDialog } = useResourceDetailsDialog()

    return (
        <>
            <Card onClick={() => setExpanded(!expanded)} className={cx(classes.card, isSelected && classes.selected)}>
                <Stack spacing="1rem" className={classes.content}>
                    {resource.imageUrl && (
                        <CardMedia
                            component="img"
                            image={resource.imageUrl}
                            alt={resource.name}
                            className={classes.image}
                        />
                    )}

                    <Stack spacing="1rem" className={classes.textContainer}>
                        <Stack direction="row" alignItems="start" justifyContent="space-between">
                            <Stack direction="column">
                                <Typography variant="subtitle1" fontWeight={500}>
                                    {resource.name} ({resource.room.sittings}){/*<Button*/}
                                    <FontAwesomeIcon
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            openResourceDetailsDialog(resource.id.toString())
                                        }}
                                        className={classes.infoButton}
                                        icon={faInfoCircle}
                                    />
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {resource.serviceCenterName}
                                </Typography>
                            </Stack>
                            <FontAwesomeIcon
                                className={classes.caretButton}
                                size="xs"
                                icon={expanded ? faCaretUp : faCaretDown}
                            />
                        </Stack>
                        <Box
                            className={cx(classes.timeBlock, {
                                [classes.timeBlockSelected]: isSelected,
                            })}
                            display="flex"
                            alignItems="center"
                        >
                            <FontAwesomeIcon icon={faClock} />
                            {bookingSuggestion && (
                                <Typography ml=".5rem" variant="body2">
                                    {dateTimeFactory.formatDate(
                                        dateTimeFactory.fromApi(bookingSuggestion.startDatetime),
                                    )}
                                    ,{" "}
                                    {dateTimeFactory.formatTime(
                                        dateTimeFactory.fromApi(bookingSuggestion.startDatetime),
                                    )}{" "}
                                    -{" "}
                                    {dateTimeFactory.formatTime(dateTimeFactory.fromApi(bookingSuggestion.endDatetime))}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Stack>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Stack direction="row" justifyContent="end" spacing="1rem" className={classes.collapseContent}>
                        {isSelected && <FontAwesomeIcon className={classes.selectedIcon} icon={faCircleCheck} />}
                        <LocalizedStrict
                            id={
                                isSelected
                                    ? "user-home-room-finder-resource-unselect-button"
                                    : "user-home-room-finder-resource-select-button"
                            }
                        >
                            <Button
                                className={cx({
                                    [classes.selectButton]: !isSelected,
                                    [classes.unselectButton]: isSelected,
                                    [classes.disabled]: isDisabled,
                                })}
                                fullWidth={false}
                                variant={isSelected ? "outlined" : "contained"}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (!isDisabled) {
                                        onSelect(resource.id)
                                    }
                                }}
                            >
                                {isSelected ? "Remove" : "Select"}
                            </Button>
                        </LocalizedStrict>
                    </Stack>
                </Collapse>
            </Card>
            {resourceId && <ResourceDetailsDialog id={resourceId} handleClose={closeResourceDetailsDialog} />}
        </>
    )
}

export function ListComponent({ availableResources, onSelect, selectedIds, onTimeSelection }: ListComponentProps) {
    const selectedRoom = availableResources?.find((item) => selectedIds.includes(item.resource.id))
    const selectedStart = selectedRoom?.bookingSuggestion?.startDatetime ?? null
    const selectedEnd = selectedRoom?.bookingSuggestion?.endDatetime ?? null

    useEffect(() => {
        onTimeSelection(selectedStart, selectedEnd)
    }, [selectedStart, selectedEnd, onTimeSelection])

    return (
        <Box display="flex" flexDirection="column" gap="0.5rem">
            {availableResources?.map((item) => {
                const isDisabled =
                    selectedIds.length > 0 &&
                    (item?.bookingSuggestion?.startDatetime !== selectedStart ||
                        item?.bookingSuggestion?.endDatetime !== selectedEnd)
                return (
                    <RoomListItem
                        key={item.resource.id}
                        {...item}
                        isDisabled={isDisabled}
                        isSelected={selectedIds.includes(item.resource.id)}
                        onSelect={onSelect}
                    />
                )
            })}
        </Box>
    )
}

export function RoomListItemSkeleton() {
    const { classes } = useStyles()

    return <Skeleton variant="rectangular" animation="wave" className={classes.skeleton} />
}
